import React, { useCallback } from "react";
import { connect } from "react-redux";

import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router-dom";

import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import { Button, Popconfirm } from "@mapmycustomers/ui";

import Path from "@app/enum/Path";
import { getActivityTypeDeletingIds } from "@app/store/activity";
import { deleteActivityType } from "@app/store/activity/actions";
import { RootState } from "@app/store/rootReducer";

import styles from "../ActivityTypesCommon.module.scss";

interface Props {
  activityType: ActivityType;
  activityTypeDeletingIds: ActivityType["id"][];
  onDeleteActivityType?: typeof deleteActivityType.request;
}

const DeleteButton: React.FC<Props> = ({
  activityType,
  activityTypeDeletingIds,
  onDeleteActivityType,
}) => {
  const intl = useIntl();

  const handleDeleteActivityType = useCallback(
    () => onDeleteActivityType?.(activityType.id),
    [activityType.id, onDeleteActivityType]
  );

  return (
    <Popconfirm
      cancelText={intl.formatMessage({
        id: "settings.activityTypes.confirmDelete.cancel",
        defaultMessage: "Wait",
        description: "Cancel text of activity type delete confirmation",
      })}
      description={
        <div className={styles.deleteConfirmTitle}>
          <div>
            {intl.formatMessage(
              {
                id: "settings.activityTypes.confirmDelete.title",
                defaultMessage:
                  "Are you sure you want to delete this activity type?<strong> Deletion of this type may affect any saved filters, reports, calendar syncs or workflows connected to this type.</strong>",
                description: "Title of activity type delete confirmation",
              },
              {
                strong: (text: string) => <strong>{text}</strong>,
              }
            )}
          </div>
          <div className={styles.message}>
            {intl.formatMessage(
              {
                id: "settings.activityTypes.confirmDelete.content",
                defaultMessage:
                  "We recommend re-assigning any activities with this type via <link>Activity List View</link> or running an import before proceeding.",
                description: "Title of activity type delete confirmation",
              },
              {
                link: (text) => <Link to={`${Path.ACTIVITY}/list`}>{text}</Link>,
              }
            )}
          </div>
        </div>
      }
      okText={intl.formatMessage({
        id: "settings.activityTypes.confirmDelete.ok",
        defaultMessage: "I'm sure",
        description: "Ok text of activity type delete confirmation",
      })}
      onConfirm={handleDeleteActivityType}
    >
      <Button
        className={styles.typeAction}
        danger
        loading={activityTypeDeletingIds.includes(activityType.id)}
      >
        <FormattedMessage
          id="settings.activityTypes.delete"
          defaultMessage="Delete"
          description="Delete button title for Activity Type"
        />
      </Button>
    </Popconfirm>
  );
};

const mapStateToProps = (state: RootState) => ({
  activityTypeDeletingIds: getActivityTypeDeletingIds(state),
});

const mapDispatchToProps = {
  onDeleteActivityType: deleteActivityType.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(DeleteButton);
